import { useReducer, useState, useEffect, useRef } from 'react';
import {
  initialScenario,
  selectVariant,
  filterVisibleChoices,
  type Choice,
  type Scenario,
  type SessionEvent,
} from '@branchworks/shared';
import {
  initPlaythrough,
  playthroughReducer,
} from './store/playthrough';
import { TitleScreen } from './screens/TitleScreen';
import { EndScreen } from './screens/EndScreen';
import { loadScenario } from './loadScenario';

/** Flow phase wrapping the node-playing session. Intro/closing are chrome —
 *  they emit no session events. */
type Phase = 'intro' | 'playing' | 'closing' | 'exited';

/** Duration of each half of the scene fade-to-black (out, then in), in ms. */
const FADE_MS = 250;

/** Honour a reduced-motion preference by skipping the fade entirely. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Phase 4b: the runtime is launchable from the editor's Preview button.
 *
 * On mount the runtime posts `branchworks:ready` to its opener (if any) and
 * listens for `branchworks:scenario` in response. If a scenario arrives, it
 * swaps in and the playthrough restarts. If no opener is present, or no
 * scenario arrives, the runtime falls back to the canonical `initialScenario`
 * — so direct loads still play the chest-pain triage scenario as a demo.
 *
 * Security: this dev build accepts the scenario message from any origin. A
 * production deploy should tighten this to the editor's known origin once we
 * have deploy-time configuration. (Same TODO as on the editor side.)
 */
export default function App() {
  const [scenario, setScenario] = useState<Scenario>(initialScenario);
  const [source, setSource] = useState<'fallback' | 'preview' | 'published' | 'embedded' | 'file'>('fallback');
  const [versionTag, setVersionTag] = useState<string | null>(null);
  // Errors from a JSON load (file pick or embedded global) that blocked play.
  const [loadError, setLoadError] = useState<string[] | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Start on the title screen when the scenario has an intro; otherwise go
  // straight to playing (exactly the pre-V6 behavior).
  const [phase, setPhase] = useState<Phase>(initialScenario.intro ? 'intro' : 'playing');

  // Scene-transition fade-to-black. A choice fades the screen out, swaps the
  // scene while black, then fades back in. 'idle' = no transition. Only choice-
  // driven scene changes fade — not the initial entry, restart, or end-screen
  // handoff (see onChoose / the handlers). Skipped entirely under reduced motion.
  const [fade, setFade] = useState<'idle' | 'out' | 'in'>('idle');
  const pendingChoiceRef = useRef<Choice | null>(null);

  // Listen for the editor's scenario message. The handshake: we tell the
  // opener we're ready, the opener responds with the scenario, we render.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'branchworks:scenario') return;
      if (!data.scenario || !data.scenario.startNodeId) return;
      // TODO: production should check event.origin against allowed editor origins.
      setScenario(data.scenario as Scenario);
      const mode = data.mode as 'fallback' | 'preview' | 'published' | undefined;
      setSource(mode || 'preview');
      setVersionTag(typeof data.versionTag === 'string' ? data.versionTag : null);
    };
    window.addEventListener('message', onMessage);

    // Announce readiness to the opener (if any).
    if (window.opener) {
      window.opener.postMessage({ type: 'branchworks:ready' }, '*');
    }

    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Embedded entry point: a standalone or SCORM bundle bakes the scenario into
  // `window.__BRANCHWORKS_SCENARIO__` at build time. If present, it wins over
  // the fallback example. Runs once; invalid JSON surfaces as a load error.
  const embeddedTried = useRef(false);
  useEffect(() => {
    if (embeddedTried.current) return;
    embeddedTried.current = true;
    const baked = window.__BRANCHWORKS_SCENARIO__;
    if (baked == null) return;
    const res = loadScenario(baked);
    if (res.ok) {
      setScenario(res.scenario);
      setSource('embedded');
    } else {
      setLoadError(res.errors);
    }
  }, []);

  const [playthrough, dispatch] = useReducer(
    playthroughReducer,
    scenario,
    initPlaythrough,
  );

  // Whenever the scenario changes (preview handoff), restart the playthrough
  // so the reducer state reflects the new scenario's start node and defaults.
  // We deliberately skip the initial run — `useReducer` already initialized
  // with the current scenario, so dispatching restart there would be a
  // redundant session start (and would double-emit events).
  const didInitialRestartSkip = useRef(false);
  useEffect(() => {
    if (!didInitialRestartSkip.current) {
      didInitialRestartSkip.current = true;
      return;
    }
    dispatch({ type: 'restart', scenario });
    setPhase(scenario.intro ? 'intro' : 'playing');
  }, [scenario]);

  // State inspector toggle. Hook lives here (above any early returns) to
  // satisfy React's rules of hooks even when the scenario has no current scene.
  const [showState, setShowState] = useState(false);
  /** Whether the Events debug pane is open. */
  const [showEvents, setShowEvents] = useState(false);

  /**
   * Subscribe to new session events. The reducer appends to
   * `playthrough.events` deterministically; this effect drains anything new
   * since the last render and logs it. Phase 5 adds an HTTP POST here.
   *
   * Strict Mode runs effects twice; using a ref to track "events we've
   * already processed" makes that safe — only the genuinely-new ones get
   * logged on a given mount.
   */
  const lastProcessedRef = useRef(0);
  useEffect(() => {
    // While the title screen is up, play hasn't started — the lazy reducer
    // init emitted a provisional session that we discard on ENTER (which
    // restarts with a fresh session id). Don't log it. Pressing ENTER moves
    // phase to 'playing' and the fresh session's events flow from here.
    if (phase === 'intro') return;
    const events = playthrough.events;
    if (events.length <= lastProcessedRef.current) {
      // No new events. (Or the reducer reset — start/restart issues a new
      // sessionId, and lastProcessedRef gets reset below on session change.)
      return;
    }
    const fresh = events.slice(lastProcessedRef.current);
    for (const event of fresh) {
      // eslint-disable-next-line no-console
      console.log('[branchworks event]', event);
      // Forward to an adapter sink if one is installed (SCORM bundle, etc.).
      window.__BRANCHWORKS_ON_EVENT__?.(event);
    }
    lastProcessedRef.current = events.length;
  }, [playthrough.events, phase]);

  // When the session id changes (new session via start/restart), reset the
  // "already processed" cursor so the new session's events flow through.
  useEffect(() => {
    lastProcessedRef.current = 0;
  }, [playthrough.sessionId]);

  // Move keyboard/AT focus to the scene heading on every scene change (WCAG
  // 2.4.3 Focus Order). When the learner advances, focus lands on the new
  // scene's title so screen readers announce it and keyboard users aren't
  // stranded on a control that no longer exists. The heading is made
  // programmatically focusable via tabIndex={-1}; programmatic focus does not
  // draw the cream ring (that's reserved for keyboard navigation), but reaching
  // the heading via the skip link does.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (phase !== 'playing') return; // title/end screens self-focus their own headings
    headingRef.current?.focus();
  }, [playthrough.currentNodeId, phase]);

  // Drive the fade. On 'out' we hold ~FADE_MS (screen goes black), then apply
  // the held choice (swap the scene while it's black) and switch to 'in'; the
  // new scene — feedback and all — fades up. focus-to-heading fires on the swap
  // (currentNodeId change), so the screen reader announces the new scene as it
  // appears. 'in' holds ~FADE_MS then returns to 'idle'.
  useEffect(() => {
    if (fade === 'out') {
      const t = setTimeout(() => {
        const c = pendingChoiceRef.current;
        pendingChoiceRef.current = null;
        if (c) dispatch({ type: 'advance', scenario, choice: c });
        setFade('in');
      }, FADE_MS);
      return () => clearTimeout(t);
    }
    if (fade === 'in') {
      const t = setTimeout(() => setFade('idle'), FADE_MS);
      return () => clearTimeout(t);
    }
  }, [fade, scenario]);

  // Preload every image in the scenario as soon as it loads (while the learner
  // is on the title screen or reading the first scene). Otherwise a scene's
  // image only starts fetching when that scene mounts, so the fade-in reveals
  // the text and the image pops in a beat later. Cached images appear with the
  // fade instead of after it.
  useEffect(() => {
    const urls = new Set<string>();
    for (const n of Object.values(scenario.nodes)) {
      for (const v of n.variants ?? []) {
        for (const m of v.media ?? []) {
          if (m.kind === 'image' && m.url) urls.add(m.url);
        }
      }
    }
    if (scenario.branding?.logoUrl) urls.add(scenario.branding.logoUrl);
    urls.forEach((u) => {
      const img = new Image();
      img.src = u;
    });
  }, [scenario]);

  // ----- Flow handlers (intro → playing → closing → exited) -----

  // ENTER from the title screen: begin a fresh session now, so session_start
  // fires at the real start moment rather than when the title screen mounted.
  const handleEnter = () => {
    dispatch({ type: 'restart', scenario });
    setPhase('playing');
  };

  // Full restart from any phase: new session, back to the title screen if the
  // scenario has one, otherwise straight to playing.
  const handleRestart = () => {
    dispatch({ type: 'restart', scenario });
    setPhase(scenario.intro ? 'intro' : 'playing');
  };

  // Advance from an outcome to the conclusion (only offered when a closing
  // screen exists). The session already completed when the outcome was entered.
  const handleContinueToClosing = () => setPhase('closing');

  // EXIT from the end screen. No LMS yet: attempt to close the window (works
  // when the runtime was opened by the editor), and fall back to an exited
  // confirmation screen. A later slice wraps this with the SCORM finish call.
  const handleExit = () => {
    // Let a SCORM adapter finish the LMS session first, if one is installed.
    window.__BRANCHWORKS_ON_EXIT__?.();
    try {
      window.close();
    } catch {
      /* ignore — browsers block close() for non-script-opened windows */
    }
    setPhase('exited');
  };

  // ----- Standalone file loading (hand a Claude-authored scenario.json to the
  // player, no editor in the loop). Only offered when the runtime is showing
  // its own content (fallback example or a previously-loaded file) — not when a
  // scenario was provided by the editor or an embedded bundle. -----
  const isStandalone = source === 'fallback' || source === 'file';

  // The editor preview is the only context that shows debug chrome — the load
  // badge, the title label, and the State/Events/Back tools. Everywhere else
  // (the bare runtime URL, a loaded file, an embedded/SCORM bundle, a published
  // version) gets a clean learner header. Load file stays available wherever the
  // runtime is showing its own content (fallback/file), so the JSON workflow
  // survives; Restart is always present.
  const isPreview = source === 'preview';

  // V7 output sizing. `pageSize.width` caps the content column (it reflows to
  // the container width below that — text wraps rather than shrinking, which
  // keeps it readable/AA on small screens). `textSize` sets the stage's base
  // font; the reading elements below are `em`-relative so they scale off it.
  // Both absent → responsive column at the default 16px, exactly as before.
  const STAGE_TEXT_PX = { small: 14, medium: 16, large: 18 } as const;
  const stageMaxWidth = scenario.display?.pageSize?.width;
  const stageTextSize = scenario.display?.textSize;
  const stageFontPx = stageTextSize ? STAGE_TEXT_PX[stageTextSize] : undefined;

  const onPickFile = () => fileRef.current?.click();

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const res = loadScenario(String(reader.result));
      if (res.ok) {
        setLoadError(null);
        setSource('file');
        setScenario(res.scenario); // triggers restart + phase reset via the scenario-change effect
      } else {
        setLoadError(res.errors);
      }
    };
    reader.onerror = () => setLoadError(['Could not read the file.']);
    reader.readAsText(file);
  };

  // Hidden file input + a dismissible error toast, included in both the title
  // and playing views so loading works from either.
  const standaloneControls = (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        onChange={onFileChosen}
        className="hidden"
      />
      {loadError && (
        <div
          role="alert"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-lg rounded border border-runtime-accent bg-runtime-bg-2 p-3 shadow-xl"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-runtime-text mb-1">
                Couldn't load that scenario
              </div>
              <ul className="text-xs text-runtime-text-2 list-disc pl-4 space-y-0.5">
                {loadError.slice(0, 6).map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
                {loadError.length > 6 && <li>…and {loadError.length - 6} more.</li>}
              </ul>
            </div>
            <button
              onClick={() => setLoadError(null)}
              className="text-runtime-text-3 hover:text-runtime-text shrink-0"
              aria-label="Dismiss"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        </div>
      )}
    </>
  );

  // ----- Phase screens (rendered instead of the playing UI) -----

  if (phase === 'intro' && scenario.intro) {
    return (
      <>
        {standaloneControls}
        {isStandalone && (
          <button
            onClick={onPickFile}
            className="fixed top-4 right-4 z-40 px-3 py-1.5 text-sm rounded border border-white/40 text-white bg-black/30 hover:bg-black/50 backdrop-blur"
          >
            Load file
          </button>
        )}
        <TitleScreen intro={scenario.intro} branding={scenario.branding} onEnter={handleEnter} />
      </>
    );
  }

  if (phase === 'closing' && scenario.closing) {
    return (
      <EndScreen
        closing={scenario.closing}
        branding={scenario.branding}
        onExit={handleExit}
        onRestart={handleRestart}
      />
    );
  }

  if (phase === 'exited') {
    return (
      <main
        aria-label="Exited"
        className="min-h-screen flex items-center justify-center p-8 text-center"
      >
        <div className="max-w-md">
          <h1 tabIndex={-1} className="text-2xl font-bold mb-3 text-runtime-text">
            You've exited the scenario.
          </h1>
          <p className="text-runtime-text-2 mb-8">You can safely close this window.</p>
          <button
            onClick={handleRestart}
            className="px-5 py-2.5 rounded bg-runtime-accent text-white text-sm font-semibold"
          >
            <span aria-hidden="true">↻</span> Start over
          </button>
        </div>
      </main>
    );
  }

  const node = scenario.nodes[playthrough.currentNodeId];
  if (!node) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-runtime-text-3 italic">
          Scenario has no current scene. (id: {playthrough.currentNodeId})
        </div>
      </div>
    );
  }

  const variant = selectVariant(node, playthrough.state);
  const visibleChoices = filterVisibleChoices(node, playthrough.state);
  const totalChoices = (node.choices || []).length;
  const hiddenCount = totalChoices - visibleChoices.length;
  const isOutcome = node.type === 'outcome';
  const hasHistory = playthrough.history.length > 0;
  const hasVariables = Object.keys(scenario.variables || {}).length > 0;

  const onChoose = (c: Choice) => {
    // Ignore clicks while a transition is already running.
    if (fade !== 'idle') return;
    // Reduced motion: swap instantly, no fade.
    if (prefersReducedMotion()) {
      dispatch({ type: 'advance', scenario, choice: c });
      return;
    }
    pendingChoiceRef.current = c;
    setFade('out');
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip link: first in tab order, visible only when focused. Jumps past
          the utility header straight to the scene heading. */}
      <a
        href="#scene-heading"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:rounded focus:bg-runtime-text focus:text-runtime-bg focus:font-semibold focus:no-underline"
      >
        Skip to scene
      </a>
      {standaloneControls}
      {/* Scene-transition fade-to-black overlay. Always mounted so the opacity
          transition animates; invisible and click-through when idle. */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-[60] bg-black pointer-events-none ease-in-out"
        style={{
          opacity: fade === 'out' ? 1 : 0,
          transitionProperty: 'opacity',
          transitionDuration: `${FADE_MS}ms`,
        }}
      />
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-3 border-b border-runtime-rule">
        <div className="flex items-center gap-3 text-sm text-runtime-text-2">
          {isPreview && (
            <span
              className="px-2 py-0.5 text-[10px] uppercase tracking-widest rounded border border-runtime-accent text-runtime-accent-text"
              title="Editor preview"
            >
              {versionTag ? `Preview · ${versionTag}` : 'Preview · Draft'}
            </span>
          )}
          {isPreview && <span>Learner runtime · {scenario.title}</span>}
        </div>
        <div className="flex items-center gap-2">
          {isStandalone && (
            <button
              onClick={onPickFile}
              className="px-3 py-1.5 text-sm rounded border border-runtime-rule text-runtime-text-2 hover:bg-runtime-bg-2"
              title="Load a scenario JSON file"
            >
              Load file
            </button>
          )}
          {isPreview && hasVariables && (
            <button
              onClick={() => setShowState((s) => !s)}
              className="px-3 py-1.5 text-sm rounded border border-runtime-rule text-runtime-text-2 hover:bg-runtime-bg-2"
              title="Toggle the variable-state inspector"
            >
              State
            </button>
          )}
          {isPreview && (
            <button
              onClick={() => setShowEvents((s) => !s)}
              className="px-3 py-1.5 text-sm rounded border border-runtime-rule text-runtime-text-2 hover:bg-runtime-bg-2"
              title="Toggle the session event log"
            >
              Events
              {playthrough.events.length > 0 && (
                <span className="ml-1.5 px-1 py-0.5 rounded text-[9px] font-bold bg-runtime-accent text-white">
                  {playthrough.events.length}
                </span>
              )}
            </button>
          )}
          {isPreview && hasHistory && (
            <button
              onClick={() => dispatch({ type: 'back' })}
              className="px-3 py-1.5 text-sm rounded text-runtime-text-2 hover:bg-runtime-bg-2"
            >
              <span aria-hidden="true">←</span> Back
            </button>
          )}
          <button
            onClick={handleRestart}
            className="px-3 py-1.5 text-sm rounded text-runtime-text-2 hover:bg-runtime-bg-2"
            title="Restart from the entry scene"
          >
            <span aria-hidden="true">↻</span> Restart
          </button>
        </div>
      </header>

      {/* Scene */}
      <main className="flex-1 flex items-center justify-center p-8 relative" aria-label="Scenario content">
        <div
          className={stageMaxWidth ? 'w-full mx-auto' : 'max-w-2xl w-full'}
          style={{
            maxWidth: stageMaxWidth ? `${stageMaxWidth}px` : undefined,
            fontSize: stageFontPx ? `${stageFontPx}px` : undefined,
          }}
        >
          <h2
            id="scene-heading"
            ref={headingRef}
            tabIndex={-1}
            className="font-bold leading-tight mb-6 text-runtime-text"
            style={{ fontSize: '1.875em' }}
          >
            {node.title}
          </h2>
          {(variant.media || []).map((m) => {
            if (m.kind === 'image') {
              return (
                <img
                  key={m.id}
                  src={m.url}
                  alt={m.alt || ''}
                  className="w-full h-auto rounded mb-6 max-h-80 object-contain"
                  onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                />
              );
            }
            if (m.kind === 'video') {
              return (
                <div key={m.id} className="mb-6">
                  {m.url && (
                    <video
                      src={m.url}
                      controls
                      aria-label={m.alt || `${node.title} — video`}
                      className="w-full rounded max-h-72"
                    >
                      {m.captionsUrl && (
                        <track kind="captions" src={m.captionsUrl} default />
                      )}
                    </video>
                  )}
                  <MediaTranscript text={m.transcript} />
                </div>
              );
            }
            // audio
            return (
              <div key={m.id} className="mb-6">
                {m.url && (
                  <audio
                    src={m.url}
                    controls
                    aria-label={m.alt || `${node.title} — audio`}
                    className="w-full"
                  >
                    {m.captionsUrl && (
                      <track kind="captions" src={m.captionsUrl} default />
                    )}
                  </audio>
                )}
                <MediaTranscript text={m.transcript} />
              </div>
            );
          })}
          <p className="leading-relaxed mb-8 text-runtime-text-2" style={{ fontSize: '1.125em' }}>
            {variant.sceneText}
          </p>

          {playthrough.pendingFeedback && (
            <div
              role="status"
              className="mb-6 p-3 rounded border border-runtime-rule bg-runtime-bg-2 italic text-runtime-text-2"
            >
              {playthrough.pendingFeedback}
            </div>
          )}

          {isOutcome ? (
            <div className="border-t pt-6 border-runtime-rule">
              <div className="flex items-center justify-end gap-3">
                {scenario.closing ? (
                  <button
                    onClick={handleContinueToClosing}
                    className="px-4 py-2 text-sm rounded bg-runtime-accent text-white font-semibold"
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    onClick={handleRestart}
                    className="px-4 py-2 text-sm rounded bg-runtime-accent text-white"
                  >
                    <span aria-hidden="true">↻</span> Try again
                  </button>
                )}
              </div>
            </div>
          ) : visibleChoices.length === 0 ? (
            <div className="text-sm italic text-runtime-text-3">
              {totalChoices === 0
                ? 'This scene has no choices yet.'
                : 'No choices are currently visible — all are gated by conditions that don\'t match the current state.'}
            </div>
          ) : (
            <div className="space-y-3" role="group" aria-label="Choices">
              {visibleChoices.map((c, i) => (
                <button
                  key={c.id || i}
                  onClick={() => onChoose(c)}
                  className="w-full text-left p-4 rounded border border-runtime-rule bg-runtime-bg-2 hover:border-runtime-accent transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <span className="font-bold text-runtime-text-3" style={{ fontSize: '1.125em' }} aria-hidden="true">
                      {i + 1}.
                    </span>
                    <div className="flex-1 text-runtime-text">{c.label}</div>
                    <span className="text-runtime-text-3 group-hover:text-runtime-accent-text" style={{ fontSize: '1.125em' }} aria-hidden="true">→</span>
                  </div>
                </button>
              ))}
              {hiddenCount > 0 && (
                <div className="text-xs italic pt-1 text-runtime-text-4">
                  {hiddenCount} choice{hiddenCount === 1 ? '' : 's'} hidden by visibility conditions.
                </div>
              )}
            </div>
          )}

          {isPreview && hasHistory && (
            <div className="mt-8 pt-6 text-xs border-t border-runtime-rule text-runtime-text-4">
              Path so far: {playthrough.history.length} step
              {playthrough.history.length === 1 ? '' : 's'}
            </div>
          )}
        </div>

        {showState && hasVariables && (
          <div className="absolute top-4 right-4 w-64 rounded border border-runtime-rule p-4 bg-runtime-bg-2">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] uppercase tracking-widest font-bold text-runtime-text-3">
                Variable state
              </span>
              <button
                onClick={() => setShowState(false)}
                className="text-runtime-text-3 hover:text-runtime-text"
                aria-label="Close state inspector"
              >
                ×
              </button>
            </div>
            <div className="space-y-1.5">
              {Object.entries(playthrough.state).map(([k, v]) => (
                <div key={k} className="flex items-baseline justify-between gap-2 text-xs font-mono">
                  <span className="truncate text-runtime-text-2">{k}</span>
                  <span className="text-runtime-accent-text">
                    {typeof v === 'string' ? `"${v}"` : String(v)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {showEvents && (
          <div
            className="absolute bottom-4 right-4 w-96 max-h-[60vh] rounded border border-runtime-rule p-4 bg-runtime-bg-2 flex flex-col"
            style={{ overflow: 'hidden' }}
          >
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
              <span className="text-[10px] uppercase tracking-widest font-bold text-runtime-text-3">
                Session events ({playthrough.events.length})
              </span>
              <button
                onClick={() => setShowEvents(false)}
                className="text-runtime-text-3 hover:text-runtime-text"
                aria-label="Close events pane"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto space-y-1.5 text-[10px] font-mono">
              {playthrough.events.length === 0 ? (
                <div className="italic text-runtime-text-4">No events yet.</div>
              ) : (
                playthrough.events.map((e) => (
                  <div
                    key={e.sequenceNumber}
                    className="px-2 py-1 rounded bg-runtime-bg border border-runtime-rule"
                  >
                    <div className="flex items-baseline justify-between gap-2 mb-0.5">
                      <span className="text-runtime-accent-text font-bold">{e.type}</span>
                      <span className="text-runtime-text-4">#{e.sequenceNumber}</span>
                    </div>
                    <div className="text-runtime-text-3 truncate" title={JSON.stringify(e)}>
                      {summarizeEvent(e)}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-3 pt-2 border-t border-runtime-rule text-[9px] text-runtime-text-4 flex-shrink-0">
              Session id: <span className="font-mono">{playthrough.sessionId}</span>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * Full text transcript for a video/audio block, rendered as a native
 * disclosure. `<details>`/`<summary>` is keyboard-operable and announces its
 * expanded/collapsed state to screen readers with no extra ARIA. Renders
 * nothing when there's no transcript. `whitespace-pre-line` preserves the line
 * breaks in authored scripts (e.g. the voicemail scenarios, where the script
 * lives entirely in the transcript and the audio URL is left empty for the
 * recordist — so this is the only content a learner gets until audio exists).
 */
function MediaTranscript({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <details className="mt-2 rounded border border-runtime-rule bg-runtime-bg-2">
      <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-runtime-text-2">
        Transcript
      </summary>
      <div className="px-3 pb-3 pt-1 text-sm leading-relaxed whitespace-pre-line text-runtime-text-2">
        {text}
      </div>
    </details>
  );
}

/**
 * One-line human summary of a session event, shown in the Events pane row.
 * The full JSON is in the row's `title` attribute for power users.
 */
function summarizeEvent(e: SessionEvent): string {
  switch (e.type) {
    case 'session_start':
      return `start: "${e.scenarioTitle}"`;
    case 'scene_entered':
      return `→ ${e.nodeTitle}`;
    case 'choice_made':
      return `picked: ${truncate(e.choiceLabel, 36)}`;
    case 'session_completed':
      return `done: ${e.outcomeLabel} (${e.stepCount} step${e.stepCount === 1 ? '' : 's'})`;
    case 'session_resumed':
      return `resumed at ${e.nodeId}`;
    default:
      return JSON.stringify(e);
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
